import { QueryClient } from "@tanstack/react-query";
import Schedule from "../components/Schedule";
import dayjs from "dayjs";
import {
  groupTournamentsBySeason,
  sortByStartDate,
} from "../utils/tournaments";
import { Tournament } from "../types/tournament";
import JoinFlowInfoModal from "../components/JoinFlowInfoModal/JoinFlowInfoModal";
import { useTmSignOut } from "../hooks/useTmSignOut";
import { useUI } from "../context/UIContext";
import { useSession } from "next-auth/react";
import { useEffect, useState } from "react";
import {
  getAccessToken,
  getTournamentOverviewList,
} from "../utils/react-query-utils";
import Head from "next/head";
import {
  getStoryblokApi,
  StoryblokComponent,
  useStoryblokState,
} from "@storyblok/react";
import { sbBlok } from "../types/sb-types";

const SchedulePageHead = ({
  seo,
  isProd,
}: {
  seo: {
    title: string;
    description: string;
    og_title: string;
    og_description: string;
    twitter_title: string;
    twitter_description: string;
  };
  isProd: boolean;
}) => {
  return (
    <Head>
      {!isProd && <meta name="robots" content="noindex, nofollow" />}
      <title>{seo?.title}</title>
      <link rel="icon" href="/favicon.ico" />
      <meta
        name="viewport"
        content="initial-scale=1.0, width=device-width, maximum-scale=1.0, user-scalable=0"
        key="viewport"
      />
      <meta name="title" content={seo?.title} />
      <meta name="description" content={seo?.description} />
      <meta property="og:title" content={seo?.og_title} />
      <meta property="og:description" content={seo?.og_description} />
      <meta property="og:image" content="/images/next_tournament_s3.jpg" />
      <meta property="twitter:title" content={seo?.twitter_title} />
      <meta property="twitter:description" content={seo?.twitter_description} />
      <meta property="twitter:image" content="/images/next_tournament_s3.jpg" />
    </Head>
  );
};

export default function SchedulePage({
  secondSeasonTournaments,
  firstSeasonTournaments,
  thirdSeasonTournaments,
  upcomingTournaments,
  isProd,
  story,
}: any) {
  story = useStoryblokState(story);

  const { state: uiState } = useUI();
  const { data: session }: { data: any; status: string } = useSession();
  const [verifyEmailFlow, setVerifyEmailFlow] = useState<string | null>("");

  useEffect(() => {
    if (verifyEmailFlow !== window.localStorage.getItem("verify_email_flow")) {
      setVerifyEmailFlow(window.localStorage.getItem("verify_email_flow"));
    }
  }, [uiState]);

  useTmSignOut();

  const scheduleCards = upcomingTournaments?.filter(
    (tournament: Tournament) => tournament?.versionInfo?.state !== "DRAFT"
  );

  return (
    <>
      <SchedulePageHead seo={story?.content?.seo} isProd={isProd} />
      <JoinFlowInfoModal
        isOpen={
          uiState.joinFlowInfoModal.isOpen &&
          ((session?.accessToken &&
            !uiState.isEmailVerified &&
            verifyEmailFlow === "true" &&
            uiState.joinFlowInfoModal.modalType === "email") ||
            uiState.joinFlowInfoModal.modalType === "signup")
        }
        tmToken={session?.accessToken}
        idTokenHint={session?.idToken}
      />
      {story?.content?.body?.map((c: sbBlok) => {
        if (c.component === "tournament_card_overview") {
          return (
            <StoryblokComponent
              key={c._uid}
              blok={c}
              tournaments={scheduleCards}
            />
          );
        }
        return <StoryblokComponent key={c._uid} blok={c} />;
      })}
      <Schedule
        firstSeasonTournaments={firstSeasonTournaments}
        secondSeasonTournaments={secondSeasonTournaments}
        thirdSeasonTournaments={thirdSeasonTournaments}
      />
    </>
  );
}

export async function getStaticProps() {
  let pastTournaments;
  let liveUpcomingTournaments;
  let draftTournaments;
  const queryClient = new QueryClient();

  const accessToken = await getAccessToken({
    processEnv: process.env.VERCEL_ENV,
    clientId: process.env.TFM_PORTAL_CLIENT_ID,
    clientIdDev: process.env.TFM_PORTAL_CLIENT_ID_DEV,
    clientSecret: process.env.TFM_PORTAL_CLIENT_SECRET,
    clientSecretDev: process.env.TFM_PORTAL_CLIENT_SECRET_DEV,
  });

  try {
    const { data: pastTournamentOverviewList } = await queryClient.fetchQuery(
      ["tournament_overview_list"],
      () =>
        getTournamentOverviewList({
          list: "HISTORY",
          processEnv: process.env.VERCEL_ENV,
          accessToken,
        })
    );
    pastTournaments = pastTournamentOverviewList;

    const { data: upcomingTournamentOverviewList } =
      await queryClient.fetchQuery(["tournament_overview_list"], () =>
        getTournamentOverviewList({
          list: "LIVE_AND_UPCOMING",
          processEnv: process.env.VERCEL_ENV,
          accessToken,
        })
      );
    liveUpcomingTournaments = upcomingTournamentOverviewList;

    const { data: draftTournamentsList } = await queryClient.fetchQuery(
      ["tournament_overview_list"],
      () =>
        getTournamentOverviewList({
          list: "DRAFT",
          processEnv: process.env.VERCEL_ENV,
          accessToken,
        })
    );
    draftTournaments = draftTournamentsList;
  } catch (err: any) {
    console.log(`===> error: ${err.message}`);
  }

  const proPastTournaments =
    pastTournaments?.data?.node?.tournaments?.list?.items?.filter(
      (tournament: Tournament) => {
        return tournament?.keyValues?.find((keyValue) => {
          return process.env.VERCEL_ENV === "production"
            ? keyValue.key === "NextTour:TournamentKind" &&
                keyValue.value.toLowerCase() === "pro"
            : keyValue.key === "NextTour:TournamentKind" &&
                (keyValue.value.toLowerCase() === "test" ||
                  keyValue.value.toLowerCase() === "pro");
        });
      }
    );

  const proLiveUpcomingTournaments =
    liveUpcomingTournaments?.data?.node?.tournaments?.list?.items?.filter(
      (tournament: Tournament) => {
        return tournament?.keyValues?.find((keyValue) => {
          return process.env.VERCEL_ENV === "production"
            ? keyValue.key === "NextTour:TournamentKind" &&
                keyValue.value.toLowerCase() === "pro"
            : keyValue.key === "NextTour:TournamentKind" &&
                (keyValue.value.toLowerCase() === "test" ||
                  keyValue.value.toLowerCase() === "pro");
        });
      }
    );

  const proDraftTournaments =
    draftTournaments?.data?.node?.tournaments?.list?.items?.filter(
      (tournament: Tournament) => {
        return tournament?.keyValues?.find((keyValue) => {
          return process.env.VERCEL_ENV === "production"
            ? keyValue.key === "NextTour:TournamentKind" &&
                keyValue.value.toLowerCase() === "pro"
            : keyValue.key === "NextTour:TournamentKind" &&
                (keyValue.value.toLowerCase() === "test" ||
                  keyValue.value.toLowerCase() === "pro");
        });
      }
    );

  const upcomingTournaments = proLiveUpcomingTournaments
    ?.filter((tournament: any) =>
      dayjs(tournament.startDate).isAfter(new Date())
    )
    ?.concat(proDraftTournaments);

  const proAllTournaments = proLiveUpcomingTournaments?.concat(
    proPastTournaments,
    proDraftTournaments
  );

  const groupedTournamentsBySeason =
    groupTournamentsBySeason(proAllTournaments);

  const firstSeason = groupedTournamentsBySeason?.filter(
    (tournament: Tournament) => tournament.season === 1
  );

  const secondSeason = groupedTournamentsBySeason?.filter(
    (tournament: Tournament) => tournament.season === 2
  );

  const thirdSeason = groupedTournamentsBySeason?.filter(
    (tournament: Tournament) => tournament.season === 3
  );

  const sbParams = {
    version: process.env.VERCEL_ENV === "production" ? "published" : "draft",
    resolve_links: "url",
  };

  const storyblokApi = getStoryblokApi();
  const { data: storyData } = await storyblokApi.get(
    "cdn/stories/schedule",
    sbParams
  );

  return {
    props: {
      story: storyData ? storyData.story : false,
      firstSeasonTournaments: sortByStartDate(firstSeason) || null,
      secondSeasonTournaments: sortByStartDate(secondSeason) || null,
      thirdSeasonTournaments: sortByStartDate(thirdSeason) || null,
      upcomingTournaments: sortByStartDate(upcomingTournaments) || null,
      isProd: process.env.VERCEL_ENV === "production",
    },
    revalidate: 5,
  };
}
